// ============================================================================
// Task create / edit modal. Used by:
//   - FacilityTasksPane → opens with facility_id pre-filled, hidden facility picker
//   - /tasks global page → opens with facility combobox visible
//   - Task detail page → edit existing task
//
// `task_id` is auto-assigned by the trigger. Frontends never supply it.
// ============================================================================

import { isFinanceSeat, isHrSeat, isSalesSeat } from '../../shared/role-gates';
import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react';
import { NO_BUILDING, NO_BUILDING_VALUE, canCreateTask, pickBuilding, taskBuilding } from './no-building';
import { X, Loader2 } from 'lucide-react';
import { useAuth } from '../../shared/auth';
import { errorMessage } from '../../shared/errors';
import { Button } from '../../shared/components';
import type {
  FacilityTask,
  TaskCategory,
  TaskPriority,
  TaskStatus,
  UserRole,
} from '../../shared/types';
import {
  createTask,
  updateTask,
  type NewTaskInput,
  type TaskEditInput,
} from './hooks/use-task-data';
import {
  useFacilityOptions,
  useEmployeeOptions,
  employeeLabel,
  orderAssignees,
  type FacilityOption,
} from './hooks/use-task-form-options';

interface Props {
  initial?: FacilityTask | null;
  presetFacility?: { facility_id: string; account_id: string | null } | null;
  open: boolean;
  onClose: () => void;
  onSaved: (row: FacilityTask, mode: 'created' | 'updated') => void;
}

const PRIORITIES: TaskPriority[] = ['High', 'Medium', 'Low'];
const STATUSES: TaskStatus[] = ['Open', 'In_Progress', 'Done', 'Deferred'];
const CATEGORIES: TaskCategory[] = [
  'Operations',
  'Finance',
  'HR',
  'Sub_Management',
  'Sales',
  'Other',
];

// Map the creator's role to a sensible category default. If the user is
// editing an existing task we keep its category instead.
function defaultCategoryForRole(role: UserRole | null | undefined): TaskCategory {
  if (isFinanceSeat(role)) return 'Finance';
  if (isHrSeat(role)) return 'HR';
  if (isSalesSeat(role)) return 'Sales';
  return 'Operations';
}

interface FormState {
  description: string;
  facility: FacilityOption | null;
  /** "Not about one building" was picked: a deliberate answer, never a blank. */
  noBuilding: boolean;
  priority: TaskPriority;
  status: TaskStatus;
  category: TaskCategory;
  dueDate: string;
  assignedTo: string | null;
  completionNotes: string;
}

function initialForm(
  initial: FacilityTask | null | undefined,
  creatorRole: UserRole | null | undefined,
): FormState {
  return {
    description: initial?.description ?? '',
    facility: null,
    noBuilding: false,
    priority: (initial?.priority as TaskPriority) ?? 'Medium',
    status: (initial?.status as TaskStatus) ?? 'Open',
    category: (initial?.category as TaskCategory | null) ?? defaultCategoryForRole(creatorRole),
    dueDate: initial?.due_date ?? '',
    assignedTo: initial?.assigned_to ?? null,
    completionNotes: initial?.completion_notes ?? '',
  };
}

export default function TaskForm({
  initial,
  presetFacility,
  open,
  onClose,
  onSaved,
}: Props) {
  const { employee, profile } = useAuth();
  const { facilities, isLoading: facsLoading } = useFacilityOptions();
  const { employees, isLoading: empsLoading } = useEmployeeOptions();
  const [form, setForm] = useState<FormState>(
    initialForm(initial ?? null, profile?.role),
  );
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // AFB-0057 (2c) — the facility's own account manager is the likeliest
  // assignee for a facility task, so pin them first. Recomputed whenever the
  // selected facility changes.
  const assigneeOptions = useMemo(
    () => orderAssignees(employees, form.facility?.account_manager_eid ?? null),
    [employees, form.facility?.account_manager_eid],
  );

  useEffect(() => {
    if (!open) return;
    const next = initialForm(initial ?? null, profile?.role);
    if (presetFacility) {
      // AFB-0057 — prefer the fully-loaded row (it carries account_manager_eid,
      // which the assignee picker needs) and fall back to the preset stub.
      const loaded = facilities.find(
        (x) => x.facility_id === presetFacility.facility_id,
      );
      next.facility = loaded ?? {
        facility_id: presetFacility.facility_id,
        account_id: presetFacility.account_id ?? null,
        facility_name: null,
        market_region: null,
        account_manager_eid: null,
      };
    } else if (initial?.facility_id) {
      const f = facilities.find((x) => x.facility_id === initial.facility_id);
      if (f) next.facility = f;
    }
    setForm(next);
    setError(null);
  }, [open, initial, presetFacility, facilities, profile?.role]);

  useEffect(() => {
    if (!open) return;
    if (initial) return;
    if (form.assignedTo) return;
    if (employee?.employee_id) {
      setForm((prev) => ({ ...prev, assignedTo: employee.employee_id }));
    }
  }, [open, initial, employee, form.assignedTo]);

  const canSubmit = useMemo(() => {
    if (submitting) return false;
    if (!form.description.trim()) return false;
    if (!initial && !canCreateTask(form)) return false;
    return true;
  }, [submitting, form, initial]);

  if (!open) return null;

  async function onSubmit(ev: FormEvent) {
    ev.preventDefault();
    if (!canSubmit) return;
    setSubmitting(true);
    setError(null);
    try {
      if (initial) {
        const patch: TaskEditInput = {
          description: form.description.trim(),
          priority: form.priority,
          status: form.status,
          category: form.category,
          due_date: form.dueDate || null,
          assigned_to: form.assignedTo,
          completion_notes: form.completionNotes || null,
        };
        await updateTask(initial.task_id, patch);
        onSaved(
          {
            ...initial,
            description: patch.description ?? initial.description,
            priority: patch.priority ?? initial.priority,
            status: patch.status ?? initial.status,
            category: patch.category ?? initial.category,
            due_date: patch.due_date ?? null,
            assigned_to: patch.assigned_to ?? null,
            completion_notes: patch.completion_notes ?? null,
          },
          'updated',
        );
      } else {
        if (!employee?.employee_id) {
          throw new Error('Sign in to create a task.');
        }
        const building = taskBuilding(form);
        const input: NewTaskInput = {
          facility_id: building.facility_id,
          account_id: building.account_id,
          description: form.description.trim(),
          priority: form.priority,
          status: form.status,
          category: form.category,
          due_date: form.dueDate || null,
          assigned_to: form.assignedTo,
          created_by: employee.employee_id,
          source: 'Manual',
          completion_notes: form.completionNotes || null,
        };
        const row = await createTask(input);
        onSaved(row, 'created');
      }
      onClose();
    } catch (err) {
      console.error('[tasks] save failed', err);
      setError(
        errorMessage(err, 'Save failed. Please try again.', 'tasks:save'),
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      className="fixed inset-0 z-50 flex items-center justify-center bg-tcs-scrim/60 p-4"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="w-full max-w-lg rounded-lg border border-tcs-border bg-tcs-raised shadow-lg">
        <div className="flex items-center justify-between border-b border-tcs-border px-4 py-3">
          <h2 className="text-sm font-semibold text-tcs-text">
            {initial ? 'Edit task' : 'New task'}
          </h2>
          <button
            type="button"
            className="rounded p-1 text-tcs-text-muted hover:bg-tcs-surface hover:text-tcs-text"
            aria-label="Close"
            onClick={onClose}
          >
            <X size={18} />
          </button>
        </div>
        <form className="space-y-4 px-4 py-4" onSubmit={onSubmit}>
          {!presetFacility && !initial && (
            <Field label="Facility" required>
              <select
                required
                disabled={facsLoading}
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.noBuilding ? NO_BUILDING_VALUE : form.facility?.facility_id ?? ''}
                onChange={(e) => {
                  const pick = pickBuilding(e.target.value, facilities);
                  setForm((prev) => ({ ...prev, ...pick }));
                }}
              >
                <option value="" disabled>
                  {facsLoading ? 'Loading facilities…' : 'Select a facility'}
                </option>
                <option value={NO_BUILDING_VALUE}>{NO_BUILDING}</option>
                {facilities.map((f) => (
                  <option key={f.facility_id} value={f.facility_id}>
                    {f.facility_name ?? f.facility_id}
                  </option>
                ))}
              </select>
            </Field>
          )}

          <Field label="Description" required>
            <textarea
              required
              rows={3}
              className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-base"
              placeholder="What needs to happen at this facility?"
              value={form.description}
              onChange={(e) =>
                setForm((prev) => ({ ...prev, description: e.target.value }))
              }
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Category">
              <select
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.category}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    category: e.target.value as TaskCategory,
                  }))
                }
              >
                {CATEGORIES.map((c) => (
                  <option key={c} value={c}>
                    {c.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Priority">
              <select
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.priority}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    priority: e.target.value as TaskPriority,
                  }))
                }
              >
                {PRIORITIES.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Status">
              <select
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.status}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    status: e.target.value as TaskStatus,
                  }))
                }
              >
                {STATUSES.map((s) => (
                  <option key={s} value={s}>
                    {s.replace('_', ' ')}
                  </option>
                ))}
              </select>
            </Field>
            <div />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Due date">
              <input
                type="date"
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.dueDate}
                onChange={(e) =>
                  setForm((prev) => ({ ...prev, dueDate: e.target.value }))
                }
              />
            </Field>
            <Field label="Assigned to">
              <select
                disabled={empsLoading}
                className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
                value={form.assignedTo ?? ''}
                onChange={(e) =>
                  setForm((prev) => ({
                    ...prev,
                    assignedTo: e.target.value || null,
                  }))
                }
              >
                <option value="">Unassigned</option>
                {/* AFB-0057 — the facility's own AM is pinned first; everyone
                    else follows alphabetically by the name shown on screen.
                    Keys carry the pin marker because the AM appears twice. */}
                {assigneeOptions.map((emp) => (
                  <option
                    key={`${emp.pinnedNote ? 'pinned-' : ''}${emp.employee_id}`}
                    value={emp.employee_id}
                  >
                    {employeeLabel(emp)}
                    {employee?.employee_id === emp.employee_id ? ' (me)' : ''}
                    {emp.pinnedNote ? ` — ${emp.pinnedNote}` : ''}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <Field label="Completion notes / context (optional)">
            <textarea
              rows={2}
              className="w-full rounded-md border border-tcs-border bg-tcs-field px-3 py-2 text-sm"
              placeholder="Internal notes, deferral reason, follow-up context…"
              value={form.completionNotes}
              onChange={(e) =>
                setForm((prev) => ({
                  ...prev,
                  completionNotes: e.target.value,
                }))
              }
            />
          </Field>

          {error && (
            <div className="rounded-md bg-st-danger-bg px-3 py-2 text-xs text-st-danger-fg">
              {error}
            </div>
          )}

          <div className="flex items-center justify-end gap-2 border-t border-tcs-border pt-3">
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit}>
              {submitting ? (
                <>
                  <Loader2 size={14} className="mr-1 animate-spin" />
                  Saving…
                </>
              ) : initial ? (
                'Save changes'
              ) : (
                'Create task'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  required,
  children,
}: {
  label: string;
  required?: boolean;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-tcs-text-muted">
        {label} {required && <span className="text-st-danger-fg">*</span>}
      </span>
      {children}
    </label>
  );
}
